// MURAL DO CLUBE — a conversa sobre o clube, fora da conversa sobre o livro.
//
// O Estudo do livro é lugar de reflexão e citação. Quando o problema é o clube
// em si — o livro cadastrado está errado, o título e o autor não batem, falta
// combinar alguma coisa —, não havia onde dizer isso: postar no Estudo suja a
// leitura de todo mundo, e fora do app só resta conhecer a pessoa por outro
// canal. O mural é esse canal, e fica no rodapé da página do clube: quem entra
// pra ler não esbarra nele, quem precisa avisar sabe onde está.
//
// Espelha a discussão da proposta (proposal-page.tsx): árvore de um nível,
// like, editar, excluir, tempo real por SSE com fallback de polling.

import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../services/apiV2';
import { AuthSession } from '../../types';
import { MentionInput } from '../mention-input';
import { MentionText } from '../mentions';

type Recado = {
  id: string; parentId: string | null;
  author: { id?: string; name?: string; avatarUrl?: string | null } | null;
  authorId: string;
  text: string | null; deleted: boolean;
  createdAt: string; editedAt: string | null;
  likes: number; likedByMe: boolean;
  replies?: Recado[];
};

function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso); if (!Number.isFinite(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'agora';
  const m = Math.floor(s / 60); if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24); if (d < 7) return `há ${d} ${d === 1 ? 'dia' : 'dias'}`;
  try { return new Date(t).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }); } catch { return ''; }
}
function hueFrom(seed: string): number { let h = 0; for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360; return h; }

const Avatar: React.FC<{ name?: string | null; url?: string | null; id?: string | null; size?: number; onClick?: () => void }> = ({ name, url, id, size = 32, onClick }) => {
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  const hue = hueFrom(id || name || '?');
  return (
    <div onClick={onClick} style={{
      flex: 'none', width: size, height: size, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.42, fontWeight: 600, color: '#fff', overflow: 'hidden', cursor: onClick ? 'pointer' : 'default',
      background: `linear-gradient(135deg, hsl(${hue},42%,42%), hsl(${hue},48%,26%))`,
    }}>{url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initial}</div>
  );
};

export const MuralDoClube: React.FC<{
  session: AuthSession | null;
  clubId: string;
  currentUserId?: string;
  myName?: string;
  myAvatarUrl?: string | null;
  /** Visitante (ainda não entrou no clube): vê o convite, não o mural. */
  isVisitor?: boolean;
  onOpenProfile?: (uid: string) => void;
}> = ({ session, clubId, currentUserId, myName, myAvatarUrl, isVisitor, onOpenProfile }) => {
  const [recados, setRecados] = useState<Recado[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [indisponivel, setIndisponivel] = useState(false);
  const [podeModerar, setPodeModerar] = useState(false);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState<{ id: string; name: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const liveRef = useRef(false);

  const recarregar = async () => {
    try {
      const r = await api.listMural(session, clubId);
      setRecados((r.comments as any) || []);
      setPodeModerar(!!r.podeModerar);
      setIndisponivel(!!r.indisponivel);
    } catch { /* sem acesso: a seção fica no estado vazio */ }
    finally { setCarregando(false); }
  };

  useEffect(() => {
    if (isVisitor) { setCarregando(false); return; }
    let vivo = true; let pollId: any = null;
    setCarregando(true);
    recarregar();
    const cancel = api.openMuralStream(session, clubId, (ev, payload) => {
      liveRef.current = true;
      if (ev === 'mural') setRecados(prev => inserir(prev, payload));
      else if (ev === 'mural:edit') setRecados(prev => mapear(prev, c => c.id === payload.id ? { ...c, text: payload.text, editedAt: new Date().toISOString() } : c));
      else if (ev === 'mural:delete') setRecados(prev => mapear(prev, c => c.id === payload.id ? { ...c, deleted: true, text: null } : c));
      else if (ev === 'mural:like') setRecados(prev => mapear(prev, c => c.id === payload.id ? { ...c, likes: payload.count } : c));
    });
    // Se em 4s o SSE não deu sinal (pm2 em cluster costuma engolir), cai pro
    // polling leve — o mural é conversa lenta, 15s é de sobra.
    const fb = setTimeout(() => { if (!liveRef.current) { pollId = setInterval(() => { if (vivo) recarregar(); }, 15000); } }, 4000);
    return () => { vivo = false; cancel(); clearTimeout(fb); if (pollId) clearInterval(pollId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, session, isVisitor]);

  const enviar = async () => {
    const t = text.trim(); if (!t || sending) return;
    setSending(true);
    try { await api.addMural(session, clubId, t, replyTo?.id || null); setText(''); setReplyTo(null); if (!liveRef.current) recarregar(); }
    catch (e: any) { window.alert(e?.message || 'Não foi possível publicar.'); }
    finally { setSending(false); }
  };
  const curtir = async (c: Recado) => {
    setRecados(prev => mapear(prev, x => x.id === c.id ? { ...x, likedByMe: !x.likedByMe, likes: x.likes + (x.likedByMe ? -1 : 1) } : x));
    try { await api.likeMural(session, clubId, c.id); }
    catch { setRecados(prev => mapear(prev, x => x.id === c.id ? { ...x, likedByMe: !x.likedByMe, likes: x.likes + (x.likedByMe ? 1 : -1) } : x)); }
  };
  const salvarEdicao = async () => {
    if (!editing) return; const t = editing.text.trim(); if (!t) return;
    try { await api.editMural(session, clubId, editing.id, t); setEditing(null); if (!liveRef.current) recarregar(); }
    catch (e: any) { window.alert(e?.message || 'Erro'); }
  };
  const excluir = async (c: Recado) => {
    if (!window.confirm('Excluir este recado?')) return;
    try { await api.deleteMural(session, clubId, c.id); if (!liveRef.current) recarregar(); }
    catch (e: any) { window.alert(e?.message || 'Erro'); }
  };
  const responder = (c: Recado) => { setReplyTo({ id: c.id, name: c.author?.name || 'recado' }); setTimeout(() => inputRef.current?.focus(), 30); };

  const total = recados.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);

  return (
    // Último bloco da página: o rodapé precisa passar da barra de abas (62px) e
    // do botão flutuante, senão o último recado fica embaixo deles.
    <div style={{ padding: '26px 20px calc(96px + env(safe-area-inset-bottom, 0px))' }}>
      <div style={{ height: 1, background: 'var(--line)', marginBottom: 20 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <div className="label">Mural do clube</div>
        {total > 0 && <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>· {total}</span>}
        {liveRef.current && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--ember)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ember)' }} /> ao vivo
          </span>
        )}
      </div>
      {/* A regra da casa, escrita: sem ela o mural vira um segundo feed de
          leitura e o Estudo perde o sentido de existir separado. */}
      <p style={{ margin: '0 0 14px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-3)' }}>
        Conversa sobre o clube — o que está errado, o que falta, o que combinar.
        As leituras ficam no Estudo.
      </p>

      {isVisitor ? (
        <div style={{ fontSize: 13.5, color: 'var(--ink-3)', padding: '4px 0 6px' }}>
          Entre no clube para conversar com os membros.
        </div>
      ) : (<>
        {/* composer */}
        <div style={{ marginBottom: 18 }}>
          {replyTo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12, color: 'var(--ink-3)' }}>
              Respondendo <strong style={{ color: 'var(--ink-2)' }}>{replyTo.name}</strong>
              <button onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', color: 'var(--ink-3)', cursor: 'pointer' }}>✕</button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Avatar name={myName} url={myAvatarUrl} id={currentUserId} size={32} />
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 8,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              borderRadius: 99, padding: '6px 6px 6px 15px',
            }}>
              <MentionInput inputRef={inputRef} value={text} onChange={setText} onEnter={enviar} dropdownPosition="above"
                placeholder="Escreva pro clube…" wrapStyle={{ flex: 1, minWidth: 0 }}
                inputStyle={{ width: '100%', background: 'none', border: 'none', outline: 'none', color: 'var(--ink)', fontSize: 13 }} />
              <button onClick={enviar} disabled={sending || !text.trim()} aria-label="Publicar recado" style={{
                flex: 'none', width: 30, height: 30, borderRadius: '50%',
                background: text.trim() ? 'var(--ember)' : 'var(--paper-3)', border: 'none',
                cursor: text.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a120c" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>
              </button>
            </div>
          </div>
        </div>

        {carregando ? (
          <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '6px 0' }}>Carregando…</div>
        ) : total === 0 ? (
          <div style={{ color: 'var(--ink-3)', fontSize: 13.5, textAlign: 'center', padding: '10px 0 4px', fontStyle: 'italic', fontFamily: "'Instrument Serif',serif" }}>
            {indisponivel ? 'O mural ainda não está aberto neste servidor.' : 'Nenhum recado ainda. Puxe o primeiro assunto.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {recados.map(c => (
              <RecadoNode key={c.id} c={c} depth={0} myId={currentUserId} podeModerar={podeModerar}
                onLike={curtir} onReply={responder} onEdit={(cc: Recado) => setEditing({ id: cc.id, text: cc.text || '' })}
                onDelete={excluir} onOpenProfile={onOpenProfile}
                editing={editing} setEditing={setEditing} saveEdit={salvarEdicao} />
            ))}
          </div>
        )}
      </>)}
    </div>
  );
};

const RecadoNode: React.FC<any> = ({ c, depth, myId, podeModerar, onLike, onReply, onEdit, onDelete, onOpenProfile, editing, setEditing, saveEdit }) => {
  const isEditing = editing?.id === c.id;
  const mine = c.authorId === myId;
  return (
    <div style={{ display: 'flex', gap: depth ? 10 : 12 }}>
      <Avatar name={c.author?.name} url={c.author?.avatarUrl} id={c.author?.id} size={depth ? 28 : 34} onClick={() => c.author?.id && onOpenProfile?.(c.author.id)} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span onClick={() => c.author?.id && onOpenProfile?.(c.author.id)} style={{ fontSize: depth ? 12.5 : 13.5, fontWeight: 600, color: 'var(--ink)', cursor: c.author?.id ? 'pointer' : 'default' }}>{c.author?.name || 'Alguém'}</span>
          <span style={{ fontSize: depth ? 10.5 : 11, color: 'var(--ink-3)' }}>{timeAgo(c.createdAt)}{c.editedAt ? ' · editado' : ''}</span>
        </div>
        {c.deleted ? (
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-3)', fontStyle: 'italic' }}>recado removido</p>
        ) : isEditing ? (
          <div style={{ marginTop: 6 }}>
            <MentionInput multiline value={editing.text} onChange={(v: string) => setEditing({ id: c.id, text: v })} rows={2} autoFocus dropdownPosition="below"
              inputStyle={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '8px 10px', color: 'var(--ink)', fontSize: 13.5 }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button onClick={() => setEditing(null)} style={{ padding: '6px 12px', borderRadius: 9, background: 'var(--paper-3)', color: 'var(--ink-2)', fontSize: 12, border: 'none', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={saveEdit} style={{ padding: '6px 12px', borderRadius: 9, background: 'var(--ember)', color: '#1a120c', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' }}>Salvar</button>
            </div>
          </div>
        ) : (
          <p style={{ margin: '4px 0 0', fontSize: depth ? 13 : 13.5, lineHeight: 1.5, color: 'var(--ink-2)', overflowWrap: 'anywhere' }}><MentionText text={c.text} /></p>
        )}
        {!c.deleted && !isEditing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
            <button onClick={() => onLike(c)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: c.likedByMe ? 'var(--ember)' : 'var(--ink-3)' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill={c.likedByMe ? 'var(--ember)' : 'none'} stroke={c.likedByMe ? 'var(--ember)' : 'currentColor'} strokeWidth="2"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" /></svg>{c.likes > 0 ? c.likes : ''}
            </button>
            {depth === 0 && <button onClick={() => onReply(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Responder</button>}
            {mine && <button onClick={() => onEdit(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: 'var(--ink-3)' }}>Editar</button>}
            {(mine || podeModerar) && <button onClick={() => onDelete(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: 'var(--ink-3)' }}>Excluir</button>}
          </div>
        )}
        {c.replies && c.replies.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
            {c.replies.map((r: Recado) => (
              <RecadoNode key={r.id} c={r} depth={depth + 1} myId={myId} podeModerar={podeModerar}
                onLike={onLike} onReply={onReply} onEdit={onEdit} onDelete={onDelete} onOpenProfile={onOpenProfile}
                editing={editing} setEditing={setEditing} saveEdit={saveEdit} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── helpers da árvore ──
function existeId(list: Recado[], id: string): boolean {
  for (const c of list) { if (c.id === id) return true; if (c.replies && existeId(c.replies, id)) return true; }
  return false;
}
function inserir(list: Recado[], item: Recado): Recado[] {
  // dedupe por id — o SSE devolve o meu próprio recado de volta
  if (existeId(list, item.id)) return list;
  if (!item.parentId) return [...list, { ...item, replies: item.replies || [] }];
  // Se o pai ainda não chegou (corrida no SSE), ignora: o polling/reload corrige.
  if (!existeId(list, item.parentId)) return list;
  const insere = (nodes: Recado[]): Recado[] => nodes.map(c => {
    if (c.id === item.parentId) return { ...c, replies: [...(c.replies || []), item] };
    if (c.replies && c.replies.length) return { ...c, replies: insere(c.replies) };
    return c;
  });
  return insere(list);
}
function mapear(list: Recado[], fn: (c: Recado) => Recado): Recado[] {
  return list.map(c => {
    const novo = fn(c);
    return novo.replies && novo.replies.length ? { ...novo, replies: mapear(novo.replies, fn) } : novo;
  });
}

export default MuralDoClube;
